// Screen: "Leaderboards"
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getLeaderboardByMetric, getRegionStats, getLeaderboardNudge } from '../apiClient';
import CrownFrameBadge from '../components/CrownFrameBadge';
import SEO from '../components/SEO';

function useIsMobile() {
  const [mobile, setMobile] = useState(window.innerWidth < 600);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 600);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
}

const METRICS = [
  { key: 'top_earners', label: 'Top Earners', icon: '💰' },
  { key: 'win_rate', label: 'Win Rate', icon: '🏆' },
  { key: 'games_played', label: 'Most Games', icon: '🎮' },
  { key: 'call_success', label: 'Best Callers', icon: '🎯' },
  { key: 'most_won', label: 'Most Won', icon: '📈' },
  { key: 'most_lost', label: 'Most Lost', icon: '📉' },
  { key: 'longest_streak', label: 'Win Streaks', icon: '🔥' },
];

const TIMEFRAMES = [
  { key: 'alltime', label: 'All Time' },
  { key: 'monthly', label: 'Monthly' },
  { key: 'weekly', label: 'Weekly' },
];

const REGIONS = [
  '', 'Northeast', 'Southeast', 'Midwest', 'Southwest', 'West Coast', 'Pacific Northwest',
  'Mid-Atlantic', 'New England', 'Great Lakes', 'Great Plains', 'Rocky Mountain', 'South'
];

export default function LeaderboardPage() {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [metric, setMetric] = useState('top_earners');
  const [timeframe, setTimeframe] = useState('alltime');
  const [region, setRegion] = useState('');
  const [data, setData] = useState([]);
  const [regionStats, setRegionStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nudge, setNudge] = useState(null);
  const { token } = useAuth();

  useEffect(() => {
    setLoading(true);
    getLeaderboardByMetric(metric, timeframe, 50, region).then(result => {
      setData(result || []);
      setLoading(false);
    });
  }, [metric, timeframe, region]);

  useEffect(() => {
    getRegionStats().then(result => setRegionStats(result || []));
    if (token) getLeaderboardNudge(token).then(n => { if (n && n.position > 0) setNudge(n); });
  }, [token]);

  const renderRow = (entry, index) => {
    const isMe = user?.userId === entry.userId;
    const rank = index + 1;
    const medal = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : `${rank}`;

    return (
      <tr key={entry.userId || index} style={isMe ? { background: '#1c2333' } : {}}>
        <td style={{ ...td, textAlign: 'center', fontSize: rank <= 3 ? 16 : 12 }}>{medal}</td>
        <td style={{ ...td, fontWeight: isMe ? 700 : 400, color: isMe ? '#58a6ff' : '#c9d1d9' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 8 }}>
            <CrownFrameBadge
              cardRankId={entry.cardRankId || entry.permanentCardRank}
              suitSymbol={entry.suitSymbol || entry.preferredSuit || '♣'}
              points={entry.activeRP ?? entry.rankPoints ?? 0}
              showPoints={false}
              size="sm"
            />
            <div style={{ minWidth: 0 }}>
              <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {entry.nickname || 'Anonymous'}
              </div>
              <div style={{ fontSize: isMobile ? 9 : 10, color: '#8b949e', marginTop: 1 }}>
                {entry.displayRank || (entry.rankName ? `${entry.rankName}` : 'Nine of Clubs')}
                {entry.activeRP ? ` · ${entry.activeRP} RP` : ''}
              </div>
            </div>
          </div>
        </td>
        {metric === 'top_earners' && (
          <>
            <td style={{ ...td, color: '#fbbf24', textAlign: 'right' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                <img src="/bucks-icon.svg" alt="" style={{ width: 12, height: 12 }} />
                {entry.balance?.toLocaleString()}
              </span>
            </td>
            {!isMobile && <td style={{ ...td, textAlign: 'right' }}>{entry.gamesPlayed}</td>}
            {!isMobile && <td style={{ ...td, textAlign: 'right', color: '#3fb950' }}>{entry.wins}</td>}
          </>
        )}
        {metric === 'win_rate' && (
          <>
            <td style={{ ...td, color: entry.winRate >= 50 ? '#3fb950' : '#f59e0b', textAlign: 'right', fontWeight: 600 }}>
              {entry.winRate}%
            </td>
            <td style={{ ...td, textAlign: 'right' }}>{entry.wins}/{entry.gamesPlayed}</td>
          </>
        )}
        {metric === 'games_played' && (
          <>
            <td style={{ ...td, color: '#58a6ff', textAlign: 'right', fontWeight: 600 }}>{entry.gamesPlayed}</td>
            {!isMobile && <td style={{ ...td, textAlign: 'right', color: '#3fb950' }}>{entry.wins}</td>}
            {!isMobile && <td style={{ ...td, textAlign: 'right' }}>{entry.winRate}%</td>}
          </>
        )}
        {metric === 'call_success' && (
          <>
            <td style={{ ...td, color: entry.successRate >= 60 ? '#3fb950' : '#f59e0b', textAlign: 'right', fontWeight: 600 }}>
              {entry.successRate}%
            </td>
            <td style={{ ...td, textAlign: 'right' }}>{entry.successfulCalls}/{entry.totalCalls}</td>
          </>
        )}
        {metric === 'most_won' && (
          <>
            <td style={{ ...td, color: '#3fb950', textAlign: 'right', fontWeight: 600 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                <img src="/bucks-icon.svg" alt="" style={{ width: 12, height: 12 }} />
                {entry.totalWon?.toLocaleString()}
              </span>
            </td>
            {!isMobile && <td style={{ ...td, textAlign: 'right', color: entry.netProfit >= 0 ? '#3fb950' : '#f85149' }}>{entry.netProfit >= 0 ? '+' : ''}{entry.netProfit?.toLocaleString()}</td>}
            {!isMobile && <td style={{ ...td, textAlign: 'right' }}>{entry.gamesPlayed}</td>}
          </>
        )}
        {metric === 'most_lost' && (
          <>
            <td style={{ ...td, color: '#f85149', textAlign: 'right', fontWeight: 600 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                <img src="/bucks-icon.svg" alt="" style={{ width: 12, height: 12 }} />
                {entry.totalLost?.toLocaleString()}
              </span>
            </td>
            {!isMobile && <td style={{ ...td, textAlign: 'right', color: entry.netProfit >= 0 ? '#3fb950' : '#f85149' }}>{entry.netProfit >= 0 ? '+' : ''}{entry.netProfit?.toLocaleString()}</td>}
            {!isMobile && <td style={{ ...td, textAlign: 'right' }}>{entry.gamesPlayed}</td>}
          </>
        )}
        {metric === 'longest_streak' && (
          <>
            <td style={{ ...td, color: '#f59e0b', textAlign: 'right', fontWeight: 700, fontSize: 16 }}>{entry.longestStreak}</td>
            {!isMobile && <td style={{ ...td, textAlign: 'right', color: entry.currentStreak > 0 ? '#3fb950' : '#8b949e' }}>{entry.currentStreak}</td>}
            {!isMobile && <td style={{ ...td, textAlign: 'right' }}>{entry.gamesPlayed}</td>}
          </>
        )}
      </tr>
    );
  };

  const th = { textAlign: 'left', padding: isMobile ? '6px 4px' : '8px 8px', color: '#8b949e', fontWeight: 600, fontSize: isMobile ? 10 : 12, whiteSpace: 'nowrap' };
  const td = { padding: isMobile ? '6px 4px' : '8px 8px', fontSize: isMobile ? 11 : 13, color: '#c9d1d9', borderTop: '1px solid #21262d' };

  const getHeaders = () => {
    switch (metric) {
      case 'top_earners':
        return (<><th style={{ ...th, textAlign: 'right' }}>Bucks</th>{!isMobile && <th style={{ ...th, textAlign: 'right' }}>Games</th>}{!isMobile && <th style={{ ...th, textAlign: 'right' }}>Wins</th>}</>);
      case 'win_rate':
        return (<><th style={{ ...th, textAlign: 'right' }}>Win %</th><th style={{ ...th, textAlign: 'right' }}>W/G</th></>);
      case 'games_played':
        return (<><th style={{ ...th, textAlign: 'right' }}>Games</th>{!isMobile && <th style={{ ...th, textAlign: 'right' }}>Wins</th>}{!isMobile && <th style={{ ...th, textAlign: 'right' }}>Win %</th>}</>);
      case 'call_success':
        return (<><th style={{ ...th, textAlign: 'right' }}>Rate</th><th style={{ ...th, textAlign: 'right' }}>S/T</th></>);
      case 'most_won':
        return (<><th style={{ ...th, textAlign: 'right' }}>Won</th>{!isMobile && <th style={{ ...th, textAlign: 'right' }}>Net</th>}{!isMobile && <th style={{ ...th, textAlign: 'right' }}>Games</th>}</>);
      case 'most_lost':
        return (<><th style={{ ...th, textAlign: 'right' }}>Lost</th>{!isMobile && <th style={{ ...th, textAlign: 'right' }}>Net</th>}{!isMobile && <th style={{ ...th, textAlign: 'right' }}>Games</th>}</>);
      case 'longest_streak':
        return (<><th style={{ ...th, textAlign: 'right' }}>Best</th>{!isMobile && <th style={{ ...th, textAlign: 'right' }}>Current</th>}{!isMobile && <th style={{ ...th, textAlign: 'right' }}>Games</th>}</>);
      default: return null;
    }
  };

  return (
    <>
    <SEO
      title="Leaderboards - Top Buck Euchre Players"
      description="See the top Buck Euchre players ranked by earnings, win rate, games played, and win streaks. Compete for the top spot."
      path="/leaderboards"
    />
    <div style={{
      padding: isMobile ? '20px 16px 40px' : '40px 24px',
      maxWidth: 700, margin: '0 auto',
      fontFamily: "'Georgia', serif", color: '#e2e8f0'
    }}>
      <h1 style={{ fontSize: isMobile ? 24 : 36, fontWeight: 700, marginBottom: isMobile ? 16 : 20 }}>Leaderboards</h1>

      {/* Your Standing nudge card */}
      {nudge && (
        <div style={{
          background: 'linear-gradient(135deg, #1a2332 0%, #0d1b2a 100%)',
          border: '1px solid #d4af37',
          borderRadius: 10,
          padding: isMobile ? '12px 14px' : '16px 20px',
          marginBottom: 16,
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 10 : 20,
          alignItems: isMobile ? 'stretch' : 'center'
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: isMobile ? 11 : 12, color: '#d4af37', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Your Standing</div>
            <div style={{ fontSize: isMobile ? 20 : 28, fontWeight: 700, color: '#fbbf24' }}>
              #{nudge.position} <span style={{ fontSize: isMobile ? 12 : 14, color: '#8b949e', fontWeight: 400 }}>of {nudge.totalPlayers} players</span>
            </div>
            <div style={{ fontSize: isMobile ? 11 : 13, color: '#94a3b8', marginTop: 4 }}>
              {nudge.winsToTop10 > 0 && <span>{nudge.winsToTop10} win{nudge.winsToTop10 !== 1 ? 's' : ''} from Top 10 &middot; </span>}
              {nudge.weeklyRPGained > 0 && <span>+{nudge.weeklyRPGained} RP this week</span>}
              {nudge.weeklyRPGained === 0 && <span>Play to earn RP this week!</span>}
            </div>
          </div>
          {nudge.nearbyRivals.length > 0 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {nudge.nearbyRivals.slice(0, isMobile ? 2 : 4).map(r => (
                <div key={r.userId} style={{
                  background: '#0d1117',
                  border: '1px solid #30363d',
                  borderRadius: 6,
                  padding: '6px 10px',
                  fontSize: isMobile ? 10 : 11,
                  minWidth: 80,
                  textAlign: 'center'
                }}>
                  <div style={{ color: '#8b949e' }}>#{r.position}</div>
                  <div style={{ color: '#c9d1d9', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 80 }}>{r.nickname}</div>
                  <div style={{ color: '#fbbf24', fontSize: 9 }}>{r.rp} RP</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Metric tabs */}
      <div style={{ display: 'flex', gap: isMobile ? 4 : 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {METRICS.map(m => (
          <button
            key={m.key}
            onClick={() => setMetric(m.key)}
            style={{
              background: metric === m.key ? '#238636' : 'transparent',
              color: metric === m.key ? '#fff' : '#8b949e',
              border: metric === m.key ? 'none' : '1px solid #30363d',
              borderRadius: 6,
              padding: isMobile ? '5px 8px' : '7px 12px',
              fontSize: isMobile ? 10 : 12,
              cursor: 'pointer',
              fontFamily: "'Georgia', serif",
              fontWeight: 600
            }}
          >
            {isMobile ? m.icon : `${m.icon} ${m.label}`}
          </button>
        ))}
      </div>

      {/* Timeframe toggle + Region filter */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: isMobile ? 16 : 20, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {TIMEFRAMES.map(t => (
            <button
              key={t.key}
              onClick={() => setTimeframe(t.key)}
              style={{
                background: timeframe === t.key ? '#1e293b' : 'transparent',
                color: timeframe === t.key ? '#e2e8f0' : '#6b7280',
                border: 'none',
                borderBottom: timeframe === t.key ? '2px solid #58a6ff' : '2px solid transparent',
                padding: '6px 12px',
                fontSize: isMobile ? 11 : 13,
                cursor: 'pointer',
                fontFamily: "'Georgia', serif"
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
        <select
          value={region}
          onChange={e => setRegion(e.target.value)}
          style={{
            background: '#0d1117', border: '1px solid #30363d', borderRadius: 6,
            color: region ? '#e2e8f0' : '#8b949e', padding: '5px 8px',
            fontSize: isMobile ? 11 : 12, cursor: 'pointer', fontFamily: "'Georgia', serif"
          }}
        >
          <option value="">All Regions</option>
          {REGIONS.filter(r => r).map(r => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div style={{
        background: '#161b22', border: '1px solid #30363d', borderRadius: 10,
        padding: isMobile ? '8px' : '12px', overflow: 'hidden'
      }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#8b949e' }}>Loading...</div>
        ) : data.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#8b949e' }}>
            No data yet. Play some games to see the leaderboard!
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...th, textAlign: 'center', width: 40 }}>#</th>
                  <th style={th}>Player & Rank</th>
                  {getHeaders()}
                </tr>
              </thead>
              <tbody>
                {data.map((entry, i) => renderRow(entry, i))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Region Stats */}
      {!region && regionStats.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <h2 style={{ fontSize: isMobile ? 16 : 20, fontWeight: 700, marginBottom: 12 }}>Region Breakdown</h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {regionStats.map(rs => (
              <button
                key={rs.region}
                onClick={() => setRegion(rs.region)}
                style={{
                  background: '#161b22', border: '1px solid #21262d', borderRadius: 8,
                  padding: isMobile ? '8px 12px' : '10px 16px', cursor: 'pointer',
                  textAlign: 'left', minWidth: isMobile ? 140 : 160
                }}
              >
                <div style={{ color: '#e2e8f0', fontSize: isMobile ? 12 : 13, fontWeight: 600, marginBottom: 4 }}>
                  {rs.region}
                </div>
                <div style={{ color: '#8b949e', fontSize: isMobile ? 10 : 11 }}>
                  {rs.totalPlayers} player{rs.totalPlayers !== 1 ? 's' : ''} &middot; {rs.totalGames} game{rs.totalGames !== 1 ? 's' : ''}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
    </>
  );
}
